<template>
  <div>
    <Header />
    <SearchBar :isDetailPage="true" @searching="onSearching" />    

    <main id="main">
      <div class="container">
        <h4 class="h4">
          <div class="loader" v-if="searching">
            <strong>Searching<span class="loader__dot">.</span><span class="loader__dot">.</span><span class="loader__dot">.</span></strong>
          </div>
        </h4>
        <div id="companyDetailHeader">
          <div class="row items-center">
            <div class="col">
              <router-link :to="{name: 'SearchResults'}">
                <a class="btn btn-link">
                  <i class="icomoon icon-chevron-left is-left" aria-hidden="true"></i>Back to Search Results
                </a>
              </router-link>
            </div>

            <div class="col-auto">
              
               <div id="share">
                <button
                  class="collapseToggle"
                  data-collapse="shareDropdown"
                  aria-expanded="false"
                  aria-controls="shareDropdown"
                >
                  <i class="icomoon icon-share is-left" aria-hidden="true"></i>Share
                  <span class="d-none d-inline-sm">:</span>
                </button>                          

                <div id="shareDropdown" class="collapse d-block-sm">
                  <ul class="list-reset">
                    <li>
                      <a :href="`mailto:?subject=${companyDetails.name}&body=${companyssrUrl}/${companyDetails.alias}`">
                        <i class="icomoon icon-email" aria-hidden="true"></i>
                        <span class="sr-only">Email</span>
                      </a>
                    </li>
                    <li>
                      <a :href="`https://twitter.com/share?text=${companyDetails.name}&url=${companyssrUrl}/${companyDetails.alias}`" target="blank">
                        <i class="icomoon icon-twitter" aria-hidden="true"></i>
                        <span class="sr-only">Twitter</span>
                      </a>
                    </li>
                    <li>
                      <a :href="`https://linkedin.com/shareArticle?mini=true&title=${companyDetails.name}&url=${companyssrUrl}/${companyDetails.alias}`" target="blank">
                        <i class="icomoon icon-linkedin" aria-hidden="true"></i>
                        <span class="sr-only">LinkedIn</span>
                      </a>
                    </li>
                    <li>
                      <a :href="`https://www.facebook.com/sharer.php?u=${companyssrUrl}/${companyDetails.alias}&t=${companyDetails.name}`" target="blank">
                        <i class="icomoon icon-facebook" aria-hidden="true"></i>
                        <span class="sr-only">Facebook</span>
                      </a>
                    </li>                   
                    <li>
                      <a :href="`https://www.pinterest.ca/pin/create/button/?url=${companyssrUrl}/${companyDetails.alias}&media=&description=${companyDetails.name}`" target="blank">
                        <i class="icomoon icon-pinterest" aria-hidden="true"></i>
                        <span class="sr-only">Pinterest</span>
                      </a>
                    </li>                   
                  </ul>
                </div>
              </div> 
            </div>
          </div>
        </div>

        <div class="company">
          <div class="row-lg">
            <div id="companyDetailContent" class="col">
              <h3 class="h3 company__title">{{ companyDetails.name }}</h3>

              <div class="row">
                <div class="col-lg-4">
                  <img
                    v-if="companyDetails.logoId"
                    class="company__image"
                    :src="`${fileUrl}${companyDetails.logoId}`"
                    alt
                  />
                  <img v-else class="company__image" src="/img/placeholder.svg" alt />

                  <div class="company__certs">
                    <p>
                      <strong>Certifications:</strong>
                    </p>
                    <ul class="list-reset">                     
                      <li v-if="companyDetails.wcbCertificateId">
                        <img class="company__certs__image" src="/img/wcb.jpg" width="20" height="20" alt />
                      </li>
                      <li v-if="companyDetails.corCertificate && companyDetails.corCertificate === true">
                        <img class="company__certs__image" src="/img/cor-seal.png" width="20" height="20" alt />
                      </li> 
                                         
                    </ul>
                  </div>
                </div>

                <div class="col-lg">
                  <h3 class="h6">Business Description</h3>
                  <p class="company__description">{{ companyDetails.businessDescription }}</p>

                  <hr />

                  <div class="row row-cols-1 row-cols-sm-3">
                    <div class="col">
                      <div class="company__serviceArea">
                        <h3 class="h6">Geographic Service Area</h3>
                        <ul>
                          <li
                            v-for="(region, index) in companyDetails.geoServiceAreas"
                            :key="`region-${index}`"
                          >{{ region }}</li>
                        </ul>
                      </div>
                    </div>

                    <div class="col">
                      <div class="company__codes">
                        <h3 class="h6">CSI Codes</h3>
                        <ul>
                          <li
                            v-for="(code, index) in companyDetails.csiCodes"
                            :key="`match-${index}`"
                          >{{ code }}</li>
                        </ul>
                      </div>
                    </div>

                    <div class="col">
                      <div class="company__classifications">
                        <h3 class="h6">Business Category</h3>
                        <ul>
                          <li v-for="build in companyDetails.businessCategories" :key="build">
                            <span>{{build}}</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <section v-if="companyDetails.companyProjectSamples && companyDetails.companyProjectSamples.length > 0">
                <h3 class="h6">Project Summaries</h3>
                <projectSummary :projects="companyDetails.companyProjectSamples" />
              </section>

              <h3 class="h6">Licenses</h3>
              <div class="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Issued By</th>
                      <th>Expiration Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="(l, i) in companyDetails.companyLicences" :key="`licence-${i}`">
                      <td>{{ l.name }}</td>
                      <td>{{ l.issuedBy }}</td>
                      <td>{{ l.expirationDate | dateonly }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 class="h6">COR / Bonding / {{ wsibName }} / Liability Insurance</h3>
              <div class="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>COR Certificate</th>
                      <th>Company Bonding</th>
                      <th>{{ wsibName }} Certificate</th>
                      <th>Company Liability Insurance</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>
                        <span v-if="companyDetails.corCertificate === true">Yes</span>
                        <span v-else-if="(companyDetails.corCertificate === false || companyDetails.corCertificate === null) && 
                              isLink2Build"></span>
                        <span v-else-if="companyDetails.corCertificate === false">No</span>
                        <span v-else>Unknown</span>
                      </td>
                      <td>
                        <div
                          v-if="companyDetails.bonded === true && !isLink2Build || 
                              (companyDetails.bonded === true && companyDetails.bondDescription && isLink2Build)"
                        >{{ companyDetails.bondDescription }}</div>
                        <div v-else-if="isLink2Build && !companyDetails.bondDescription"></div>
                        <div v-else-if="companyDetails.bonded === false">No</div>
                        <div v-else>Unknown</div>
                      </td>
                      <td>
                        <span v-if="companyDetails.wcbCertificateId">
                          <a
                            :href="`${fileUrl}${companyDetails.wcbCertificateId}`"
                            :title="downloadTitle"
                            @click.prevent="downloadWcbCertificate()"
                          >Download certificate</a>
                        </span>
                        <span v-else-if="isLink2Build"></span>
                        <span v-else>No</span>
                      </td>
                      <td>
                        <span
                          v-if="companyDetails.liabilityInsurance"
                        >${{ companyDetails.liabilityInsurance }}</span>
                        <span v-else-if="isLink2Build"></span>
                        <span v-else>No</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 class="h6" v-if="isLink2Build && companyDetails.companyAssociations &&companyDetails.companyAssociations.length > 0">Association and Memberships</h3>
              <div class="table-responsive" v-if="isLink2Build && companyDetails.companyAssociations &&companyDetails.companyAssociations.length > 0">
                <table>
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th>Expiration Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="(a, i) in companyDetails.companyAssociations"
                      :key="`association-${i}`"
                    >
                      <td>{{ a.name }}</td>
                      <td>{{ a.expirationDate | dateonly }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 class="h6" v-if="isLink2Build && companyDetails.companyUnionAffiliations && companyDetails.companyUnionAffiliations.length > 0">Union Affiliations</h3>
              <div class="table-responsive" v-if="isLink2Build && companyDetails.companyUnionAffiliations && companyDetails.companyUnionAffiliations.length > 0">
                <table>
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th>Expiration Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="(u, i) in companyDetails.companyUnionAffiliations"
                      :key="`affiliation-${i}`"
                    >
                      <td>{{ u.name }}</td>
                      <td>{{ u.expirationDate | dateonly }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div id="companyDetailContact" class="col-lg-auto">
              <h2 class="h6">Contact</h2>
              <ul class="list-icons">
                <li v-if="companyDetails.website">
                  <i class="icomoon icon-globe" aria-hidden="true"></i>
                  <a
                    :href="'//' + removeProtocol(companyDetails.website)"
                    target="_blank"
                    alt="..."
                  >{{ removeProtocol(companyDetails.website) }}</a>
                </li>
                <li v-if="companyDetails.phone">
                  <i class="icomoon icon-phone" aria-hidden="true"></i>
                  <a href="#">{{ companyDetails.phone }}</a>
                </li>
                <li v-if="companyDetails.email">
                  <i class="icomoon icon-email" aria-hidden="true"></i>
                  <a :href="'mailto:' + companyDetails.email">{{ companyDetails.email }}</a>
                </li>
                <li>
                  <i class="icomoon icon-map-pin" aria-hidden="true"></i>
                  <span>
                    {{companyDetails.address.street}} <br/> {{addressCityProv}}
                  </span>
                </li>                              
              </ul>
              <ul v-if="companyDetails.companyContactTypes.length > 0" class="list-icons">
                <li v-for="(s, index) in companyDetails.companyContactTypes" :key="`type-${index}`">
                 <i :class="'icomoon icon-'+s.socialMediaTypeName.toLowerCase()"  aria-hidden="true"></i>                  
                    <a :href="'//' + removeProtocol(s.url)" target="_blank" alt="">{{ removeProtocol(s.url) }}</a>                 
                </li>
              </ul>
              <ul v-if="companyDetails.users.length > 0" class="list-icons">
                <li v-for="(u, index) in companyDetails.users" :key="`user-${index}`">
                 <i class="icomoon icon-email" aria-hidden="true"></i>
                  <span>
                    <a :href="'mailto:' + u.email">{{ u.fullName }}</a><br/>
                    {{u.title}}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import projectSummary from "@/views/company/components/project-summary";
import Header from "@/components/header.vue";
import SearchBar from "@/components/search-bar.vue";

import axios from "axios";

export default {
  name: "CompanyDetails",
  data() {
    return {
      companyDetails: {},
      message: String,
      fileUrl: `${process.env.VUE_APP_API_URL}api/v1/file/`,
      companyssrUrl: `${window.location.origin}/companies`,
      searching:false,
      downloadTitle: 'Download ' +  this.wsibName  + ' Certificate',
    };
  },
  props: {
    alias: {
      type: String,
      default: ""
    }
  },
  components: { projectSummary, Header, SearchBar },
  async created() {
    await this.loadCompayDetails();
  },
  computed: {
    wsibName(){
     return process.env.VUE_APP_TENANT_NAME.toLowerCase() === 'link2build' ? 'WSIB' : 'WCB'
    },
    isLink2Build(){
      return process.env.VUE_APP_TENANT_NAME.toLowerCase().replaceAll(' ','') === 'link2build'
    },
    addressCityProv() {
      let address = "";      
      if(!this.companyDetails.address)
        return  address;

      if(this.companyDetails.address.city && this.companyDetails.address.city.length > 0)
        address += this.companyDetails.address.city;
      if(this.companyDetails.address.abbProvince && this.companyDetails.address.abbProvince.length > 0)
        address += `, ${this.companyDetails.address.abbProvince}`;
      if(this.companyDetails.address.postalCode && this.companyDetails.address.postalCode.length > 0)
        address += `, ${this.companyDetails.address.postalCode}`;

      return  address;
    }
  },
  mounted() {
    document.body.classList.add("companyDetail");
  },
  destroyed() {
    document.body.classList.remove("companyDetail");
  },
  methods: {
    async getCompanyDetails() {
      try {
        let url = process.env.VUE_APP_API_URL + "api/v1/" + this.alias;
        let response = await axios.get(url);
        return response.data;
      } catch (error) {
        this.message = error;
      }
    },
    downloadWcbCertificate () {
       axios.get(`${this.fileUrl}${this.companyDetails.wcbCertificateId}`, { responseType: 'blob' })
        .then(response => {
          const blob = new Blob([response.data], { type: 'application/pdf' })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = this.wsibName+".pdf";
          link.click()
          URL.revokeObjectURL(link.href)
        }).catch( error => {
            throw(error);
          });       
    },
    async loadCompayDetails() {
      this.companyDetails = {};
      this.companyDetails = await this.getCompanyDetails();
    },
    removeProtocol: function(url) {
      return url.replace(/(^\w+:|^)\/\//, "");
    },
    onSearching(value){
      this.searching = value;
    }
  },
};
</script>

<style scoped>
</style>
